import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconContainerProps,
  Paper,
  Rating,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import { ChangeRatingDto, TraineeEvaluationDto } from "../../../types/applicationTypes";
import {
  Cancel,
  CheckCircle,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../../utils/authUtils";

/**
 * The styled Rating component is used to style the rating icons
 */
const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    color: theme.palette.action.disabled,
  },
}));

/**
 * The customIcons object contains the icons for the different ratings
 */
const customIcons: {
  [index: string]: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon: React.ReactElement<any>;
    label: string;
  };
} = {
  1: {
    icon: <SentimentVeryDissatisfied color="error" />,
    label: "Very Dissatisfied",
  },
  2: {
    icon: <SentimentSatisfied color="warning" />,
    label: "Neutral",
  },
  3: {
    icon: <SentimentVerySatisfied color="success" />,
    label: "Very Satisfied",
  },
};

/**
 * The interface TraineeApplicantsTableProps defines the props for the TraineeApplicantsTable component
 */
interface TraineeApplicantsTableProps {
  applicantsEvaluations: TraineeEvaluationDto[];
  deleteApplicant: (traineeApplicantId: number) => void;
  updateTraineeEvaluation: (evaluation: ChangeRatingDto) => void;
}

/**
 * The TraineeApplicantsTable component displays the table with the trainee applicants and the evaluations
 * @param applicantsEvaluations The evaluations of the trainee applicants
 * @param deleteApplicant The function to delete a trainee applicant
 * @param updateTraineeEvaluation The function to update the evaluation of a trainee
 * @returns The TraineeApplicantsTable component
 */
const TraineeApplicantsTable = ({
  applicantsEvaluations,
  deleteApplicant,
  updateTraineeEvaluation,
}: TraineeApplicantsTableProps) => {
  const { auth } = useContext(AuthContext);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedApplicant, setSelectedApplicant] = React.useState<TraineeEvaluationDto | null>(null);
  const hasAdminPermission = doesPermissionsHaveSomeOf(auth.permissions, [16]);

  const styles = {
    tableHeader: {
      backgroundColor: "primary.main",
      color: "primary.contrastText",
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    tableHeaderCell: {
      color: "white",
      fontWeight: "bold",
    },
    tableRow: {
      "&:nth-of-type(odd)": { backgroundColor: "#fff" },
      "&:nth-of-type(even)": { backgroundColor: "#ededed" },
      textDecoration: "none",
    },
  };

  /**
   * The IconContainer component displays the rating icons
   * @param props The props for the IconContainer component
   * @returns The IconContainer component
   */
  const IconContainer = (props: IconContainerProps) => {
    const { value, ...other } = props;
    return <span {...other}>{customIcons[value].icon}</span>;
  };

  /**
   * The handleUpdateRating function updates the rating of a trainee
   * @param traineeApplicantId The id of the trainee applicant
   * @param rating The new rating
   */
  const handleUpdateRating = (traineeApplicantId: number, rating: number | null) => {
    if (auth.userID === null) return;
    updateTraineeEvaluation({ traineeApplicantId, memberId: auth.userID, evaluation: rating });
  };

  /**
   * The handleSelectApplicantToDelete function selects a trainee applicant to delete
   * @param traineeApplicant The trainee applicant to delete
   */
  const handleSelectApplicantToDelete = (traineeApplicant: TraineeEvaluationDto) => {
    setSelectedApplicant(traineeApplicant);
    setDeleteDialogOpen(true);
  };

  /**
   * The handleDeleteApplicant function deletes a trainee applicant
   */
  const handleDeleteApplicant = () => {
    if (selectedApplicant) {
      deleteApplicant(selectedApplicant.traineeApplicantId);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Stack>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du wirklich die Bewerbung von {selectedApplicant?.firstName} {selectedApplicant?.lastName} löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
            <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteApplicant}>
              Löschen
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" fontWeight={"bold"} textAlign={"start"} marginBottom={2}>
        Traineebewerbungen ({applicantsEvaluations.length})
      </Typography>
      <Typography>
        Es sind bereits {applicantsEvaluations.length} Bewerbungen eingegangen. Bitte gib hier deine Bewertungen ab.
        Alle Änderungen werden sofort gespeichert.
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={styles.tableHeader}>
            <TableRow>
              <TableCell sx={styles.tableHeaderCell}>Bewerber</TableCell>
              <TableCell sx={styles.tableHeaderCell}>Meine Bewertung</TableCell>
              <TableCell sx={styles.tableHeaderCell}>
                <Grid container direction="column">
                  <Grid item>
                    <Typography fontWeight={"bold"} fontSize={14} textAlign={"center"}>
                      Bewerbungsgespräch möglich
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography fontWeight={"bold"} fontSize={14} textAlign={"center"}>
                          Freitag
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography fontWeight={"bold"} fontSize={14} textAlign={"center"}>
                          Samstag
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography fontWeight={"bold"} fontSize={14} textAlign={"center"}>
                          Sonntag
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TableCell>
              <TableCell sx={styles.tableHeaderCell}>Dabei beim WW?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applicantsEvaluations.map((evaluation, index) => (
              <TableRow key={`${evaluation.firstName}-${evaluation.lastName}-${index}`} sx={styles.tableRow}>
                <TableCell>
                  <Link
                    to={`/traineebewerbungen/${evaluation.traineeApplicantId}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {evaluation.lastName + ", " + evaluation.firstName}
                  </Link>
                </TableCell>
                <TableCell>
                  <StyledRating
                    defaultValue={evaluation.evaluations.find((e) => e.memberId === auth?.userID)?.evaluation}
                    IconContainerComponent={IconContainer}
                    max={3}
                    highlightSelectedOnly
                    onChange={(event, newValue) => {
                      handleUpdateRating(evaluation.traineeApplicantId, newValue);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Grid container>
                    <Grid item xs={4}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        {evaluation.availabilitySelectionWeekend === "nichtFR" ? (
                          <Cancel color="error" />
                        ) : (
                          <CheckCircle color="success" />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        {evaluation.availabilitySelectionWeekend === "nichtSA" ? (
                          <Cancel color="error" />
                        ) : (
                          <CheckCircle color="success" />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={4} justifyContent={"center"}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        {evaluation.availabilitySelectionWeekend === "nichtSO" ? (
                          <Cancel color="error" />
                        ) : (
                          <CheckCircle color="success" />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell>
                  <Stack alignItems={"center"} direction={"row"} justifyContent={"space-between"}>
                    {evaluation.workingWeekend ? (
                      <CheckCircle color="success" sx={{ pl: 5 }} />
                    ) : (
                      <Cancel color="error" sx={{ pl: 5 }} />
                    )}
                    {evaluation.toBeAdmitted
                      ? "aufgenommen"
                      : hasAdminPermission && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              handleSelectApplicantToDelete(evaluation);
                            }}
                          >
                            Löschen
                          </Button>
                        )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default TraineeApplicantsTable;
